import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { Box, Typography, useTheme, Paper, Stack } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface StackConfig {
  key: string;
  label?: string;
  color: string;
}

interface FleetStackedBarChartProps {
  title: string;
  unit?: string;
  masterData: Record<string, any>;
  approvedData: Record<string, any>;
  height?: number;
  stackConfig?: StackConfig[]; // defines order, label, and color
}

const formatLabel = (key: string): string =>
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const aggregateFleetValues = (data: Record<string, any>) => {
  const keysToAvoid = ['bumper_number', 'total'];
  const total: Record<string, number> = {};
  Object.values(data).forEach((entry: any) => {
    Object.entries(entry).forEach(([key, value]) => {
      if (!keysToAvoid.includes(key) && typeof value === 'number') {
        total[key] = (total[key] || 0) + value;
      }
    });
  });
  return total;
};

const CustomTooltip = ({ active, payload, label, stacks, unit }: any) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;

  return (
    <Paper
      elevation={2}
      sx={{
        p: 1,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        fontSize: '0.75rem',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
        {label}
      </Typography>
      {stacks.map((stackItem: any) => {
        const match = payload.find((p: any) => p.name === stackItem.key);
        if (!match) return null;

        const formattedValue =
          typeof match.value === 'number'
            ? match.value.toFixed(2)
            : !isNaN(parseFloat(match.value))
            ? parseFloat(match.value).toFixed(2)
            : match.value;

        return (
          <Box
            key={stackItem.key}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: stackItem.color,
              }}
            />
            <Box display={'flex'} gap={'2px'} alignItems={'center'}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                {`${stackItem.label}: ${formattedValue}`}
              </Typography>
              {unit ? (
                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                  {`${unit ?? ''}`}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
};

export const FleetKPIStackedBarChart: React.FC<FleetStackedBarChartProps> = ({
  title,
  unit = '',
  masterData,
  approvedData,
  height = 300,
  stackConfig,
}) => {
  const theme = useTheme();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const aggregatedMaster = useMemo(
    () => aggregateFleetValues(masterData),
    [masterData]
  );
  const aggregatedApproved = useMemo(
    () => aggregateFleetValues(approvedData),
    [approvedData]
  );

  // Determine stack order and colors
  const fallbackKeys = Array.from(
    new Set([
      ...Object.keys(aggregatedMaster),
      ...Object.keys(aggregatedApproved),
    ])
  );

  const stacks = stackConfig?.length
    ? stackConfig.map((item) => ({
        key: item.key,
        label: item.label ?? formatLabel(item.key),
        color: item.color,
      }))
    : fallbackKeys.map((key, index) => ({
        key,
        label: formatLabel(key),
        color: [
          palette.primary,
          '#34D399',
          '#FBBF24',
          '#F87171',
          '#A78BFA',
          '#F472B6',
          '#38BDF8',
        ][index % 7],
      }));

  const chartData = [
    { name: 'Actual Data', ...aggregatedMaster },
    { name: 'Approved Plan', ...aggregatedApproved },
  ];

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'customBackground.section',
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, fontSize: '0.9rem' }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          flex: '0 0 auto',
          height: height - 80,
          minHeight: 180,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="4 4"
              stroke={theme.palette.divider}
            />
            <XAxis
              dataKey="name"
              tick={{ fill: theme.palette.text.primary, fontSize: 10 }}
            />
            <YAxis
              tick={{ fill: theme.palette.text.primary, fontSize: 10 }}
              label={{
                value: unit ? `(${unit})` : '',
                angle: -90,
                position: 'insideLeft',
                fill: theme.palette.text.secondary,
                fontSize: 10,
              }}
            />
            <Tooltip
              cursor={{
                fill: enableDarkTheme
                  ? palette.sectionBackground
                  : 'rgba(225,225,225,.8)',
              }}
              content={<CustomTooltip stacks={stacks} unit={unit} />}
            />

            {[...stacks].reverse().map((stack, index, arr) => {
              const isTopStack = index === arr.length - 1; // last rendered bar = top stack

              return (
                <Bar
                  key={stack.key}
                  dataKey={stack.key}
                  stackId="a"
                  fill={stack.color}
                  radius={isTopStack ? [4, 4, 0, 0] : [0, 0, 0, 0]} // only round top layer
                  isAnimationActive={false}
                  barSize={70}
                >
                  <LabelList
                    dataKey={stack.key}
                    position="centerBottom"
                    fill="#fff"
                    fontSize={10}
                    content={(props: any) => {
                      const { x, y, width, height, value } = props;
                      if (height < 15 || width < 20) return null;
                      return (
                        <text
                          x={(Number(x) || 0) + (Number(width) || 0) / 2}
                          y={(Number(y) || 0) + (Number(height) || 0) / 2}
                          textAnchor="middle"
                          fill="#fff"
                          fontSize="10"
                        >
                          {Number(value).toFixed(1)}
                        </text>
                      );
                    }}
                  />
                </Bar>
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box>
        <Stack
          direction="row"
          flexWrap="wrap"
          rowGap={0.1}
          columnGap={1.5}
          sx={{ justifyContent: 'flex-start' }}
        >
          {stacks.map((stack) => (
            <Box
              key={stack.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: stack.color,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  color: theme.palette.text.primary,
                }}
              >
                {stack.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};
